import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BarChart3, TrendingUp, Users } from 'lucide-react';
import { getGroupAnalytics } from '../../../redux/userGroups/groups/userGroupies';
import { selectGroupAnalytics } from '../../../redux/userGroups/groups/groupReducer';

const EngagementBar = ({ entry, max }) => {
  const { t } = useTranslation();
  const height = max > 0 ? Math.max(4, Math.round((entry.engagement / max) * 64)) : 4;
  return (
    <div className="flex flex-col items-center justify-end" title={t('groups.analytics.engagementTooltip', { date: entry.date, count: entry.engagement })}>
      <div className="w-2 bg-blue-500/70 rounded-t" style={{ height: `${height}px` }} />
    </div>
  );
};

const GroupAnalytics = ({ groupId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const analytics = useSelector(selectGroupAnalytics);

  useEffect(() => {
    if (groupId) dispatch(getGroupAnalytics(groupId));
  }, [dispatch, groupId]);

  if (!analytics) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const { stock_breakdown: stockBreakdown = [], activity_metrics: activity = {} } = analytics;
  const trend = activity.engagement_trend || [];
  const growth = activity.member_growth || [];
  const maxEngagement = Math.max(1, ...trend.map((t) => t.engagement));
  const totalNewMembers = growth.reduce((sum, g) => sum + g.new_members, 0);
  const currentMembers = growth.length ? growth[growth.length - 1].total_members : 0;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-cream-100 dark:bg-gray-900/50 rounded-xl p-4 border border-cream-300 dark:border-gray-700/50">
          <div className="text-gray-600 dark:text-gray-400 text-sm mb-1">{t('groups.analytics.totalPosts')}</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{activity.total_posts ?? 0}</div>
        </div>
        <div className="bg-cream-100 dark:bg-gray-900/50 rounded-xl p-4 border border-cream-300 dark:border-gray-700/50">
          <div className="text-gray-600 dark:text-gray-400 text-sm mb-1">{t('groups.analytics.posts30d')}</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{activity.posts_last_30_days ?? 0}</div>
        </div>
        <div className="bg-cream-100 dark:bg-gray-900/50 rounded-xl p-4 border border-cream-300 dark:border-gray-700/50">
          <div className="text-gray-600 dark:text-gray-400 text-sm mb-1">{t('groups.analytics.newMembers30d')}</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{totalNewMembers} <span className="text-sm text-gray-500 dark:text-gray-500 font-normal">{t('groups.analytics.ofCount', { count: currentMembers })}</span></div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
          <BarChart3 size={18} className="text-blue-400" />
          <span>{t('groups.analytics.engagementHeading')}</span>
        </h3>
        {trend.length > 0 ? (
          <div className="flex items-end space-x-1 h-16 bg-cream-100 dark:bg-gray-900/30 rounded-xl p-3 border border-cream-300 dark:border-gray-700/50 overflow-x-auto">
            {trend.map((entry) => (
              <EngagementBar key={entry.date} entry={entry} max={maxEngagement} />
            ))}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-500 text-sm">{t('groups.analytics.noActivityRecorded')}</p>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
          <TrendingUp size={18} className="text-blue-400" />
          <span>{t('groups.analytics.trackedStocks')}</span>
        </h3>
        {stockBreakdown.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {stockBreakdown.map((s) => (
              <div key={s.symbol} className="bg-cream-100 dark:bg-gray-900/50 rounded-xl p-4 border border-cream-300 dark:border-gray-700/50">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900 dark:text-white">{s.symbol}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-500">{s.sector}</span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{s.name}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-500 text-sm">{t('groups.analytics.noStocksTagged')}</p>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
          <Users size={18} className="text-blue-400" />
          <span>{t('groups.analytics.memberGrowthHeading')}</span>
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          {t('groups.analytics.memberGrowthSummary', {
            newMemberPhrase: t('groups.analytics.newMemberCount', { count: totalNewMembers }),
            totalMemberPhrase: t('groups.analytics.totalMemberCount', { count: currentMembers }),
          })}
        </p>
      </div>
    </div>
  );
};

export default GroupAnalytics;
